import React, { useCallback, useState } from 'react';
import { Content, EmptyState, EmptyStateBody, Label, Tooltip } from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr, TreeRowWrapper } from '@patternfly/react-table';
import { CubeIcon } from '@patternfly/react-icons';
import { formatBytes } from '../../../utils/formatBytes';
import { formatWallClockDateTime } from '../../../utils/dateFormat';
import type { BackupChainMember, BackupDiskChain } from '../../../types/models/Backup';

export interface BackupDiskChainsTableProps {
  chains: BackupDiskChain[];
}

/**
 * The subject's qcow2 **chains** — a base image plus one increment per run — as a two-level tree:
 * a chain per parent row, its members underneath.
 *
 * Two things this table deliberately gets right, because both are easy to get wrong:
 *
 * - The same `disk` can legitimately appear **more than once** with different `chain_started_at`
 *   values. That is a re-seed, not a duplicate row, which is also why `disk_count` counts chains
 *   rather than disks. Rows are keyed by chain id, never by `disk`.
 * - `base_modified_at` tracks **retention**, not creation: the base is rewritten whenever the
 *   oldest increment is committed into it, so it is usually *newer* than most increments. It is
 *   labelled "last rewritten" and never presented as a creation date. `chain_started_at` is the
 *   real chain age.
 *
 * `members` is an unvalidated pass-through blob the backend decodes and echoes, so its shape is
 * checked rather than assumed.
 */
const BackupDiskChainsTable: React.FunctionComponent<BackupDiskChainsTableProps> = ({ chains }) => {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set<number>());

  const toggleExpanded = useCallback((chainId: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(chainId)) {
        next.delete(chainId);
      } else {
        next.add(chainId);
      }
      return next;
    });
  }, []);

  if (chains.length === 0) {
    return (
      <EmptyState titleText="No disk chains" icon={CubeIcon} variant="sm" headingLevel="h3">
        <EmptyStateBody>The last scan found no qcow2 chains in this directory.</EmptyStateBody>
      </EmptyState>
    );
  }

  const rows: React.ReactNode[] = [];
  let rowIndex = 0;

  chains.forEach((chain, chainIndex) => {
    const members: BackupChainMember[] = Array.isArray(chain.members) ? chain.members : [];
    const expanded = expandedIds.has(chain.id);
    // The chevron renders only when aria-setsize > 0, so a chain with no member blob gets none.
    const chainProps = {
      isExpanded: expanded,
      isHidden: false,
      'aria-level': 1,
      'aria-posinset': chainIndex + 1,
      'aria-setsize': members.length,
      toggleAriaLabel: `Toggle members of ${chain.disk}`,
    };

    rows.push(
      <TreeRowWrapper key={`chain-${chain.id}`} row={{ props: chainProps }} className="ancalagon-backup-vm-header">
        <Td
          dataLabel="Chain"
          treeRow={{ onCollapse: () => toggleExpanded(chain.id), props: chainProps, rowIndex: rowIndex++ }}
        >
          {chain.disk}
        </Td>
        <Td dataLabel="Disk">{chain.disk_label ?? '—'}</Td>
        <Td dataLabel="Chain started">{formatWallClockDateTime(chain.chain_started_at)}</Td>
        <Td dataLabel="Members">{chain.member_count}</Td>
        <Td dataLabel="Total size">
          <Tooltip
            content={`${formatBytes(chain.total_allocated_bytes)} allocated. Base image ${formatBytes(
              chain.base_size_bytes,
            )}, last rewritten ${formatWallClockDateTime(chain.base_modified_at)} — the base is rewritten when the oldest increment is committed into it, so that is a retention date, not a creation date.`}
          >
            <span>{formatBytes(chain.total_size_bytes)}</span>
          </Tooltip>
        </Td>
        <Td dataLabel="Newest member">{formatWallClockDateTime(chain.newest_member_at)}</Td>
      </TreeRowWrapper>,
    );

    if (!expanded) {
      return;
    }

    members.forEach((member, memberIndex) => {
      const memberProps = {
        isExpanded: false,
        isHidden: false,
        'aria-level': 2,
        'aria-posinset': memberIndex + 1,
        'aria-setsize': 0,
      };
      return rows.push(
        <TreeRowWrapper key={`chain-${chain.id}-member-${memberIndex}`} row={{ props: memberProps }}>
          <Td
            dataLabel="Member"
            treeRow={{ onCollapse: () => undefined, props: memberProps, rowIndex: rowIndex++ }}
          >
            {member.is_base ? (
              <Label isCompact color="blue">
                Base #{member.sequence}
              </Label>
            ) : (
              <Content component="small">Increment #{member.sequence}</Content>
            )}
          </Td>
          <Td />
          <Td dataLabel="Modified">{formatWallClockDateTime(member.modified_at)}</Td>
          <Td />
          <Td dataLabel="Size">
            <Tooltip content={`${formatBytes(member.allocated_bytes)} allocated`}>
              <span>{formatBytes(member.size_bytes)}</span>
            </Tooltip>
          </Td>
          <Td />
        </TreeRowWrapper>,
      );
    });
  });

  return (
    <Table isTreeTable aria-label="Disk chains" variant="compact">
      <Thead>
        <Tr>
          <Th>Chain</Th>
          <Th>Disk</Th>
          <Th>Chain started</Th>
          <Th>Members</Th>
          <Th>Total size</Th>
          <Th>Newest member</Th>
        </Tr>
      </Thead>
      <Tbody>{rows}</Tbody>
    </Table>
  );
};

export default BackupDiskChainsTable;
